/**
 * AI Customer Chat - 聊天组件
 * 支持Web/移动端，兼容Vue/Nuxt3/jQuery环境
 * 实现为Web Components
 */

// 使用自执行函数避免污染全局作用域
(function (global) {
  // API配置
  const API_CONFIG = {
    get baseUrl() {
      // 根据当前URL判断环境
      const isTestEnv = window.location.href.includes("test-");
      const domain = isTestEnv ? "test-api.kmf.com" : "api.kmf.com";
      return `https://${domain}/agi/api/grade/sse/ky-ai-dialogue`;
    },
    get headers() {
      // 动态获取认证token
      const getAuthToken = () => {
        // 尝试从cookie中获取认证信息
        const cookies = document.cookie.split(";");
        for (let cookie of cookies) {
          const [name, value] = cookie.trim().split("=");
          if (
            name === "authkey" ||
            name === "FDX_token" ||
            name === "tal_token"
          ) {
            return value;
          }
        }
        // 如果cookie中没有，尝试从localStorage获取
        return (
          localStorage.getItem("authkey") ||
          localStorage.getItem("FDX_token") ||
          localStorage.getItem("tal_token") ||
          ""
        );
      };

      return {
        Authorization: getAuthToken(),
        "Content-Type": "application/json",
      };
    },
  };

  // 增强: UUID生成和管理工具
  const UUIDManager = {
    // 生成UUID v4
    generateUUID() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
        /[xy]/g,
        function (c) {
          const r = (Math.random() * 16) | 0;
          const v = c === "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        }
      );
    },

    // 获取主域名
    getMainDomain() {
      const hostname = window.location.hostname;

      // 如果是localhost或IP地址，直接返回
      if (hostname === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
        return hostname;
      }

      // 分割域名
      const parts = hostname.split(".");

      // 如果只有一级域名，直接返回
      if (parts.length <= 2) {
        return hostname;
      }

      // 返回主域名（最后两级）
      return "." + parts.slice(-2).join(".");
    },

    // 从cookie获取值
    getCookieValue(name) {
      const cookies = document.cookie.split(";");
      for (let cookie of cookies) {
        const [cookieName, cookieValue] = cookie.trim().split("=");
        if (cookieName === name) {
          return cookieValue;
        }
      }
      return null;
    },

    // 设置永久有效的cookie到主域
    setCookie(name, value) {
      const domain = this.getMainDomain();

      try {
        // 构建cookie字符串，设置为永久有效（2038年）
        let cookieString = `${name}=${value}; expires=Tue, 19 Jan 2038 03:14:07 GMT; path=/; SameSite=Lax`;

        // 如果不是localhost或IP地址，设置domain
        if (domain.startsWith(".")) {
          cookieString += `; domain=${domain}`;
        }

        document.cookie = cookieString;

        console.log(
          `UUID已保存到永久cookie: ${name}=${value}, domain=${domain}`
        );
      } catch (e) {
        console.warn("无法设置cookie:", e);
      }
    },

    // 获取或生成用户UUID，仅使用cookie存储
    getUserUUID() {
      const cookieName = "ai_customer_uuid";
      let uuid = null;

      // 从cookie获取
      uuid = this.getCookieValue(cookieName);

      // 如果没有，生成新的UUID并保存到cookie
      if (!uuid) {
        uuid = this.generateUUID();
        this.setCookie(cookieName, uuid);
        console.log("已生成新的UUID并保存到永久cookie:", uuid);
      }

      return uuid;
    },

    // 获取设备指纹信息（可选，用于增强唯一性识别）
    getDeviceFingerprint() {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      ctx.textBaseline = "top";
      ctx.font = "14px Arial";
      ctx.fillText("Device fingerprint", 2, 2);

      return {
        screen: `${screen.width}x${screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
        platform: navigator.platform,
        userAgent: navigator.userAgent.substring(0, 100), // 截取前100字符避免过长
        canvasFingerprint: canvas.toDataURL().substring(0, 50), // 截取前50字符
      };
    },

    // 生成增强的用户标识（包含设备指纹）
    getEnhancedUserIdentifier() {
      const uuid = this.getUserUUID();
      const fingerprint = this.getDeviceFingerprint();

      // 生成设备指纹哈希
      const fingerprintString = JSON.stringify(fingerprint);
      let hash = 0;
      for (let i = 0; i < fingerprintString.length; i++) {
        const char = fingerprintString.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // 转换为32位整数
      }

      return {
        uuid: uuid,
        deviceHash: Math.abs(hash).toString(16),
        timestamp: Date.now(),
      };
    },
  };

  // 更新获取用户UUID的函数
  const getUserUUID = () => {
    return UUIDManager.getUserUUID();
  };

  // 内联样式模块而非动态加载
  const AiCustomerChatStyles = {
    /**
     * 生成聊天组件的CSS样式
     * @param {Object} options - 配置选项
     * @param {boolean} isMobile - 是否为移动设备
     * @returns {string} CSS样式字符串
     */
    generateStyles(options, isMobile) {
      const position = options.position || "right-bottom";
      const primaryColor = options.primaryColor || "#2196F3";
      const headerBackgroundImage =
        options.headerBackgroundImage ||
        "https://image.kmf.com/test/3e/1e/3e1eca053deb1edddbaca3c374390f6d.png";

      // 获取客服头像URL
      const botAvatarUrl =
        options.botAvatarUrl ||
        "https://image.kmf.com/test/8a/a1/8aa120a462e2e4333ab627f7cc217211.png";

      // 判断是否显示浮动按钮
      const showFloatButton = options.showFloatButton !== false;

      return `
        .ai-customer-chat-container {
          position: fixed;
          z-index: 9999;
          ${position === "right-bottom" ? "right: 20px; bottom: 20px;" : ""}
        }
        
        .ai-customer-chat-button {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background-color: ${primaryColor};
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 2px 5px rgba(0,0,0,0.2);
          transition: all 0.3s;
        }
        
        .ai-customer-chat-button:hover {
          transform: scale(1.05);
        }
        
        .ai-customer-chat-box {
          display: none;
          flex-direction: column;
          border-radius: 20px;
          border: 2px solid #FFFFFF;
          box-sizeing: border-box;
          box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.08);
          background: white;
          box-shadow: 0 5px 15px rgba(0,0,0,0.1);
          overflow: visible; /* 允许内容溢出 */
          transition: all 0.3s ease;
          position: relative; /* 添加相对定位以便子元素绝对定位 */
        }
        
        .ai-customer-chat-box.desktop {
          position: fixed;
          right: 20px;
          ${
            showFloatButton ? "bottom: 100px;" : "bottom: 20px;"
          } /* 根据是否有浮动按钮调整位置 */
          width: 600px;
          height: calc(100vh - 180px);
          max-height: 600px; /* 最大高度 */
        }
        
        .ai-customer-chat-box.mobile {
          position: fixed;
          left: 0;
          bottom: 0;
          width: 100%;
          height: 85vh; /* 增加高度以显示更多内容 */
          border-radius: 15px 15px 0 0; /* 增加上边角的圆角 */
          transform: translateY(100%);
          max-width: 100%; /* 确保不会超出屏幕 */
        }
        
        .ai-customer-chat-box.open {
          display: flex;
        }
        
        .ai-customer-chat-box.mobile.open {
          transform: translateY(0);
        }

        /* 当没有浮动按钮时，为聊天框添加额外的定位样式 */
        ${
          !showFloatButton
            ? `
        .ai-customer-chat-box.desktop.no-float-button {
          bottom: 20px;
          /* 可以根据需要添加其他样式调整 */
        }
        `
            : ""
        }

        .ai-customer-chat-messages {
          flex: 1;
          padding: 15px 30px;
          overflow-y: auto; /* 仅让消息区域滚动 */
          overflow-x:hidden; /* 禁止水平滚动 */
          background: white;
          border-radius: 0 0 10px 10px;
          position: relative;
          z-index: 1; /* 确保它在装饰图片之上 */
          background:url('https://image.kmf.com/test/18/9f/189f6dffc1e489539393b4e109a466bb.png') no-repeat top center;
          background-size: contain;
           /* 自定义滚动条样式 */
          scrollbar-width: thin; /* Firefox */
          scrollbar-color: #d8d8d8 transparent; /* Firefox */
        }
         /* Webkit浏览器的滚动条样式 */
        .ai-customer-chat-messages::-webkit-scrollbar {
          width: 6px; /* 滚动条宽度 */
        }
        
        .ai-customer-chat-messages::-webkit-scrollbar-track {
          background: transparent; /* 滚动条轨道背景 */
          border-radius: 3px;
        }
        
        .ai-customer-chat-messages::-webkit-scrollbar-thumb {
          background: #d8d8d8; /* 滚动条颜色 */
          border-radius: 3px;
          transition: background 0.3s ease;
        }
        
        .ai-customer-chat-messages::-webkit-scrollbar-thumb:hover {
          background: #d8d8d8; /* 悬停时颜色 */
        }
        
        .ai-customer-chat-messages::-webkit-scrollbar-thumb:active {
          background: #d8d8d8; /* 拖拽时颜色 */
        }
        
        /* 移动端滚动条优化 */
        .ai-customer-chat-box.mobile .ai-customer-chat-messages {
          padding: 15px;
          /* 移动端使用更细的滚动条 */
          scrollbar-width: thin;
          scrollbar-color: #d8d8d8 transparent;
        }
        
        .ai-customer-chat-box.mobile .ai-customer-chat-messages::-webkit-scrollbar {
          width: 4px; /* 移动端更细的滚动条 */
        }
        
        .ai-customer-chat-box.mobile .ai-customer-chat-messages::-webkit-scrollbar-thumb {
          background: #d8d8d8;
          border-radius: 2px;
        }
        
        .ai-customer-chat-box.mobile .ai-customer-chat-messages::-webkit-scrollbar-thumb:hover {
          background: #d8d8d8;
        }
        .ai-customer-chat-header {
          ${
            headerBackgroundImage
              ? `background-image: url('${headerBackgroundImage}');
          background-size: contain;`
              : `background: ${primaryColor};`
          }
          color: white;
          display: flex;
          justify-content: space-between;
          align-items: center;
          height:60px;
          background-repeat: no-repeat;
          position: relative;
          z-index: 2;
          border-radius: 10px 10px 0 0;
          overflow: visible; /* 允许内容溢出 */
        }
        
        .ai-customer-chat-header::after {
            content: '';
            position: absolute;
            top: -28px; /* 调整位置以适应超出部分 */
            right: 30px;
            width: 90px;
            height: 92px;
            background:url('https://image.kmf.com/test/e9/02/e902a013c31fd8781fb26d3a64a49021.png') no-repeat;
            background-size: 90px 92px;
            z-index: 3; /* 确保它显示在顶层 */
            pointer-events: none; /* 防止图片阻止点击事件 */
        }
        
        .ai-customer-chat-header-content {
          display: flex;
          align-items: center;
          flex: 1;
        }
        
        .ai-customer-chat-title-image {
          height: 30px;
          margin-right: 10px;
        }
        
        .ai-customer-chat-title {
          height: 26px;
          margin-right: 20px;
          margin-left: 40px;
        }
        
        .ai-customer-chat-description {
          font-size: 14px;
          align-self: center;
          color:#00085B;
        }
        
        .ai-customer-chat-welcome {
          background: #f1f1f1;
          border-radius: 10px;
          padding: 10px;
          margin-bottom: 10px;
          max-width: 80%;
          margin-left: 40px;
        }
          .ai-customer-chat-welcome-options{
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 20px !important;
          font-size: 14px;
          color: #00085B;
          }
        .ai-customer-chat-welcome-options span{
          background: #ffffff;
          border-radius: 6px;
          padding: 5px 10px;
          cursor: pointer;
          transition: background 0.3s;
          }
        .ai-customer-chat-message {
          margin-bottom: 10px;
          max-width: 90%;
          width: fit-content;
        }
          .ai-customer-chat-message p{
          margin: 0;
          padding: 0;
          font-size:14px;
          line-height: 1.5;
          }
        
        .ai-customer-chat-message.user {
          margin-left: auto;
         background: #2B9BEB;
        border-radius: 12px 0px 12px 12px;
        border: 1px solid #FFFFFF;
            color: white;
          padding: 15px 20px;
          max-width: calc(90% - 52px);
        }
        
        .ai-customer-chat-message.bot {
          display: flex;
          align-items: flex-start;
          text-align: left;
          margin-right: auto;
        }
        
        .ai-customer-chat-bot-avatar {
          width: 52px;
          height: 52px;
          border-radius: 50%;
          margin-right: 10px;
          flex-shrink: 0; /* 防止头像被挤压 */
          background: url('${botAvatarUrl}') no-repeat center;
          background-size: cover;
        }
        
        .ai-customer-chat-bot-content {
          border-radius: 10px;
          padding: 15px 20px;
          background: #DAF2FF;
            border-radius: 0px 12px 12px 12px;
            border: 1px solid #FFFFFF;
          flex-grow: 1;
            color: #00085B;
            font-size:14px;
            line-height: 1.5;
        }
      .ai-customer-chat-bot-content img{
        max-width: 120px !important;
        }
        
        .ai-customer-chat-input {
          display: flex;
          padding: 10px 20px;
          position: relative;
          z-index: 2;
          background: white;
          border-radius:0 0 20px 20px;
          align-items: center;
        }
        
        .ai-customer-chat-input-wrapper {
          position: relative;
          flex: 1;
          margin-right: 10px;
          display: flex;
        }
        
         .ai-customer-chat-input textarea {
          width: 100%;
          border: 1px solid #2B9BEB;
          border-radius: 22px;
          padding: 12px 40px 12px 15px;
          resize: none;
          min-height: 20px;
          max-height: 60px; /* 约3行的高度 */
          line-height: 20px;
          box-sizing: border-box;
          font-family: inherit;
          font-size: 14px;
          overflow-y: hidden; /* 初始隐藏滚动条 */
          transition: height 0.1s ease;
           /* 隐藏滚动条但保持滚动功能 */
          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none; /* IE and Edge */
        }
          /* 隐藏 Webkit 浏览器的滚动条 */
        .ai-customer-chat-input textarea::-webkit-scrollbar {
          display: none;
        }
            /* 新增: textarea获取焦点时的样式 */
        .ai-customer-chat-input textarea:focus {
          border-color: #2B9BEB !important;
          outline: none;
          box-shadow: 0 0 0 2px rgba(43, 155, 235, 0.2);
        }
        
         /* 添加placeholder样式 */
        .ai-customer-chat-input textarea::placeholder {
          color: #BAC0FF;
          opacity: 1;
        }
        
        
        /* Firefox */
        .ai-customer-chat-input textarea::-moz-placeholder {
          color: #BAC0FF;
          opacity: 1;
        }
        
        /* Internet Explorer 10-11 */
        .ai-customer-chat-input textarea:-ms-input-placeholder {
          color: #BAC0FF;
        }
        
        /* Microsoft Edge */
        .ai-customer-chat-input textarea::-ms-input-placeholder {
          color: #BAC0FF;
        }

        
        .ai-customer-chat-send {
          position: absolute;
          right: 15px;
          top: 50%;
          transform: translateY(-50%);
          width: 18px;
          height: 18px;
          background: transparent url('https://image.kmf.com/test/51/6f/516fc651db753450e83aa61ef9927cd9.png') no-repeat center;
          background-size: contain;
          border: none;
          padding: 0;
          cursor: pointer;
          font-size: 0; /* 隐藏文字 */
        }
        
        .ai-customer-chat-new {
           width: 90px;
            height: 44px;
            border-radius: 22px;
            border: 1px solid ${primaryColor};
            text-align: center;
          background: white;
          cursor: pointer;
        color: #4D8DFF;
          flex-shrink: 0; /* 防止按钮被挤压 */
        }
        
        .ai-customer-chat-close{
            width: 22px;
            height: 22px;
            background:url('https://image.kmf.com/test/ad/23/ad23f7cf3d3ae86999cf96a270634e56.png') no-repeat;
            background-size: cover;
            position: absolute;
            right:0;
            top:0;
            cursor: pointer;
        }
            .ai-customer-chat-box.mobile .ai-customer-chat-header{
            height:73px;
            background:url('https://image.kmf.com/test/99/d4/99d4854694c0715842761ba27f4f036d.png') no-repeat;
            background-size: cover;
            }
            .ai-customer-chat-box.mobile .ai-customer-chat-header::after{
            display: none; /* 移动端隐藏装饰图片 */
            }
            .ai-customer-chat-box.mobile .ai-customer-chat-header-content{
            flex-direction: column;
            align-items: flex-start;
            }
            .ai-customer-chat-box.mobile .ai-customer-chat-description{
                align-self: flex-start;
                margin-top: 5px;
            }
                .ai-customer-chat-box.mobile .ai-customer-chat-messages{
          padding:15px;
                }
          .ai-customer-chat-box.mobile .ai-customer-chat-new{
          border:0;
          height: 20px;
          }
        /* 移动端特定样式 */
        @media (max-width: 768px) {
          /* 移动端调整消息区域内边距 */

          
          /* 移动端调整机器人头像尺寸 */
          .ai-customer-chat-bot-avatar {
            width: 40px;
            height: 40px;
          }
          
          /* 移动端欢迎选项样式优化 */
          .ai-customer-chat-welcome-options {
            flex-direction: column;
            gap: 8px;
          }
          
          .ai-customer-chat-welcome-options span {
            padding: 8px 12px;
            font-size: 14px;
            text-align: center;
            display: inline-block;
            background: #F5F7FF;
            border: 1px solid #E0E7FF;
          }
          
          /* 移动端消息样式优化 */
          .ai-customer-chat-message {
            max-width: 85%;
          }
          
          /* 移动端输入区域调整 */
          .ai-customer-chat-input {
            padding: 8px 10px;
            flex-direction: column;
          }
          
          .ai-customer-chat-input-wrapper {
            margin-right: 0;
            margin-bottom: 8px;
            width: 100%;
          }
           .ai-customer-chat-input textarea {
            padding: 10px 40px 10px 12px;
            border-radius: 18px;
            font-size: 16px; /* 防止iOS缩放 */
          }
          .ai-customer-chat-new {
            width: 100%; /* 移动端新对话按钮占满宽度 */
            height: 40px;
          }
          
          /* 移动端头部调整 */
          .ai-customer-chat-header {
            height: 65px;
            padding: 0 15px 0 20px;
          }
          
          .ai-customer-chat-header::after {
            top: -30px;
            right: 10px;
            width: 90px;
            height: 90px;
          }
          
          /* 移动端标题和描述调整 */
          .ai-customer-chat-title {
            height: 20px;
            margin-right: 10px;
          }
          
          .ai-customer-chat-description {
            font-size: 12px;
          }
        }
        
        /* 尺寸特别小的设备适配 */
        @media (max-width: 350px) {
          .ai-customer-chat-message {
            max-width: 95%;
          }
          
          .ai-customer-chat-bot-avatar {
            width: 32px;
            height: 32px;
            margin-right: 5px;
          }
          
          .ai-customer-chat-bot-content,
          .ai-customer-chat-message.user {
            padding: 10px;
          }
        }

        /* 新增：打字机效果和加载动画样式 */
        .ai-customer-chat-typing-indicator {
          display: inline-block;
          animation: blink 1s infinite;
          color: #999;
          font-weight: bold;
          margin-left: 2px;
        }
        
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        
        .ai-customer-chat-loading {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 15px 20px;
          background: #DAF2FF;
          border-radius: 0px 12px 12px 12px;
          border: 1px solid #FFFFFF;
          color: #00085B;
          font-size: 14px;
          margin-bottom: 5px;
        }
        
        .ai-customer-chat-loading-dots {
          display: flex;
          gap: 3px;
        }
        
        .ai-customer-chat-loading-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background-color: #2B9BEB;
          animation: loadingPulse 1.4s infinite ease-in-out both;
        }
        
        .ai-customer-chat-loading-dot:nth-child(1) {
          animation-delay: -0.32s;
        }
        
        .ai-customer-chat-loading-dot:nth-child(2) {
          animation-delay: -0.16s;
        }
        
        @keyframes loadingPulse {
          0%, 80%, 100% {
            transform: scale(0.8);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        /* 流式文本的打字机效果 */
        .streaming-text {
          display: inline;
          position: relative;
        }
        
        .streaming-text::after {
          content: '|';
          animation: typing-cursor 1s infinite;
          color: #2B9BEB;
          font-weight: bold;
        }
        
        @keyframes typing-cursor {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        
        /* 完成状态时移除光标 */
        .streaming-text.completed::after {
          display: none;
        }
        
        /* 新增：消息淡入动画 */
        .ai-customer-chat-message {
          margin-bottom: 10px;
          max-width: 90%;
          width: fit-content;
          animation: messageSlideIn 0.3s ease-out;
        }
        
        @keyframes messageSlideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `;
    },

    /**
     * 将样式添加到页面中
     * @param {Object} options - 配置选项
     * @param {boolean} isMobile - 是否为移动设备
     */
    addStylesToDocument(options, isMobile) {
      const styleId = "ai-customer-chat-styles";

      // 如果已存在样式，则先移除
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }

      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = this.generateStyles(options, isMobile);
      document.head.appendChild(style);
    },
  };

  // 将样式模块添加到全局对象
  global.AiCustomerChatStyles = AiCustomerChatStyles;

  // Web Component定义
  class AiCustomerChatElement extends HTMLElement {
    constructor() {
      super();
      // 创建Shadow DOM
      this.attachShadow({ mode: "open" });

      // 解析属性配置 - 修复布尔属性处理
      this.options = {
        autoOpen: this.hasAttribute("auto-open"),
        buttonSelector: this.getAttribute("button-selector"),
        position: this.getAttribute("position") || "right-bottom",
        title: this.getAttribute("title-image"),
        titleDescription:
          this.getAttribute("title-description") || "24小时解答你的考研问题",
        placeholder: this.getAttribute("placeholder") || "请输入您的问题...",
        primaryColor: this.getAttribute("primary-color") || "#2196F3",
        // 修复showFloatButton属性解析，支持多种写法
        showFloatButton:
          this._parseBooleanAttribute("showFloatButton") &&
          this._parseBooleanAttribute("showfloatbutton") &&
          !this.hasAttribute("hide-float-button"),
        buttonImageUrl: this.getAttribute("button-image"),
        headerBackgroundImage: this.getAttribute("header-background"),
        botAvatarUrl: this.getAttribute("bot-avatar-url"),
      };

      this.isMobile = window.innerWidth <= 768;
      this.isOpen = false;
      this.conversationId = ""; // 对话ID
      this.isProcessing = false; // 是否正在处理请求
      // 新增: 流式请求控制
      this.currentAbortController = null; // 当前请求的取消控制器
      this.currentReader = null; // 当前流式读取器
    }
  // 修改: 终止当前对话，增加错误处理
    _abortCurrentConversation() {
      console.log('正在终止当前对话...');
      
      // 1. 取消当前的网络请求
      if (this.currentAbortController) {
        try {
          this.currentAbortController.abort();
        } catch (error) {
          console.warn('取消网络请求时出错:', error);
        }
        this.currentAbortController = null;
        console.log('已取消当前网络请求');
      }
      
      // 2. 关闭当前的流式读取器
      if (this.currentReader) {
        try {
          // 先检查读取器状态
          if (!this.currentReader.closed) {
            this.currentReader.cancel().catch(error => {
              // 忽略取消时的错误，这是正常的
              if (error.name !== 'AbortError') {
                console.warn('取消读取器时出错:', error);
              }
            });
          }
          this.currentReader.releaseLock();
        } catch (error) {
          // 忽略 AbortError 和其他预期的错误
          if (error.name !== 'AbortError') {
            console.warn('关闭流式读取器时出错:', error);
          }
        }
        this.currentReader = null;
        console.log('已关闭流式读取器');
      }
      
      // 3. 重置处理状态
      this.isProcessing = false;
      
      // 4. 移除任何加载指示器
      this._removeLoadingIndicators();
      
      // 5. 移除任何流式效果
      this._removeStreamingEffects();
      
      console.log('对话终止完成');
    }
     // 修改: 移除所有加载指示器，增加安全检查
    _removeLoadingIndicators() {
      const messagesContainer = this.shadowRoot.querySelector('.ai-customer-chat-messages');
      if (!messagesContainer) return;
      
      // 移除所有加载消息
      const loadingMessages = messagesContainer.querySelectorAll('.loading-message');
      loadingMessages.forEach(element => {
        this._safeRemoveElement(element);
      });
      
      console.log(`已移除 ${loadingMessages.length} 个加载指示器`);
    }
     // 新增: 组件销毁时清理资源，增加错误处理
    disconnectedCallback() {
      console.log('组件被销毁，清理资源');
      try {
        this._abortCurrentConversation();
      } catch (error) {
        console.warn('清理资源时出错:', error);
      }
    }
      // 新增: 移除所有流式效果
    _removeStreamingEffects() {
      const messagesContainer = this.shadowRoot.querySelector('.ai-customer-chat-messages');
      if (!messagesContainer) return;
      
      // 移除所有流式文本效果
      const streamingElements = messagesContainer.querySelectorAll('.streaming-text');
      streamingElements.forEach(element => {
        element.classList.remove('streaming-text');
        element.classList.add('completed');
      });
      
      console.log(`已移除 ${streamingElements.length} 个流式效果`);
    }
    // 新增: 解析布尔属性的方法
    _parseBooleanAttribute(attrName) {
      // 检查多种可能的属性名格式
      const variations = [
        attrName, // 原始名称
        attrName.toLowerCase(), // 全小写
        this._camelToKebab(attrName), // 转换为kebab-case
      ];

      for (const variation of variations) {
        if (this.hasAttribute(variation)) {
          const value = this.getAttribute(variation);
          // 如果属性存在但没有值，或者值为true/""，则为true
          if (value === null || value === "" || value === "true") {
            return true;
          }
          // 如果值为false，则为false
          if (value === "false") {
            return false;
          }
        }
      }

      // 默认返回true（Web Components默认行为）
      return true;
    }

    // 新增: 将camelCase转换为kebab-case
    _camelToKebab(str) {
      return str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, "$1-$2").toLowerCase();
    }

    // 当属性发生变化时调用
    static get observedAttributes() {
      return [
        "auto-open",
        "button-selector",
        "position",
        "title-image",
        "title-description",
        "placeholder",
        "primary-color",
        "showfloatbutton", // 支持小写
        "show-float-button", // 支持kebab-case
        "hide-float-button",
        "button-image",
        "header-background",
        "bot-avatar-url",
      ];
    }

    // 当属性变化时重新解析配置
    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue !== newValue) {
        // 重新解析showFloatButton属性
        if (
          name === "showfloatbutton" ||
          name === "show-float-button" ||
          name === "hide-float-button"
        ) {
          this.options.showFloatButton =
            this._parseBooleanAttribute("showFloatButton") &&
            this._parseBooleanAttribute("showfloatbutton") &&
            !this.hasAttribute("hide-float-button");

          // 如果组件已初始化，重新创建
          if (this.shadowRoot && this.shadowRoot.children.length > 0) {
            this.reinitialize();
          }
        }
      }
    }

    // 新增: 重新初始化组件
    reinitialize() {
      // 清除现有内容
      this.shadowRoot.innerHTML = "";
      // 重新初始化
      this.initComponent();
    }

    // 当元素被添加到DOM中时调用
    connectedCallback() {
      this.initComponent();
    }

    // 组件初始化
    initComponent() {
      // 创建样式标签
      const style = document.createElement("style");
      style.textContent = AiCustomerChatStyles.generateStyles(
        this.options,
        this.isMobile
      );
      this.shadowRoot.appendChild(style);

      // 创建组件DOM结构
      this.createChatComponent();

      // 绑定事件
      this.bindEvents();

      // 更新欢迎消息，添加机器人头像
      this.updateWelcomeMessage();

      // 如果设置了自动打开，立即打开
      if (this.options.autoOpen) {
        // 移除延迟，立即打开
        this.open();
      }
    }

    // 简化移动端检测方法
    isMobileView() {
      return window.innerWidth <= 768;
    }

    // 添加缺失的isMobileView方法
    isMobileView() {
      return window.innerWidth <= 768;
    }

    // 新增: 绑定欢迎选项点击事件
    _bindWelcomeOptions() {
      const welcomeOptions = this.shadowRoot.querySelectorAll(
        ".ai-customer-chat-welcome-options span"
      );

      welcomeOptions.forEach((option) => {
        option.addEventListener("click", () => {
          const optionText = option.textContent.trim();
          this._handleOptionClick(optionText);
        });
      });
    }

     // 修改: 处理选项点击，添加终止逻辑
    _handleOptionClick(optionText) {
      console.log('点击欢迎语选项，准备终止当前对话');
      
      // 1. 先终止当前对话（如果有）
      this._abortCurrentConversation();
      
      // 2. 重置对话ID，确保每次选择选项都开始新对话
      this.conversationId = "";
      
      // 3. 短暂延迟后开始新对话，确保终止操作完成
      setTimeout(() => {
        // 创建一个用户消息，内容为选项文本
        const messagesContainer = this.shadowRoot.querySelector(".ai-customer-chat-messages");
        const messageElement = document.createElement("div");
        messageElement.className = "ai-customer-chat-message user";
        messageElement.innerHTML = `<p>${optionText}</p>`;
        messagesContainer.appendChild(messageElement);

        // 用户点击选项时使用平滑滚动
        this._smoothScrollToBottom(messagesContainer);

        // 触发消息发送事件
        this.dispatchEvent(
          new CustomEvent("message", {
            detail: { message: optionText, type: "user" },
          })
        );

        // 发送API请求处理选项
        this._sendToAPI(optionText, "option");

        console.log("选择欢迎语选项，开始新对话，conversationId已重置为空:", this.conversationId);
      }, 100);
    }


    // 新增: 处理选项的回复
    _processOptionResponse(option) {
      // 删除原有的模拟回复逻辑，改为API调用
      // 具体回复将通过API获取
    }

    // 更新欢迎消息，添加机器人头像和添加可点击的选项
    updateWelcomeMessage() {
      const messagesContainer = this.shadowRoot.querySelector(
        ".ai-customer-chat-messages"
      );
      const welcomeMsg = this.shadowRoot.querySelector(
        ".ai-customer-chat-welcome"
      );

      if (welcomeMsg) {
        // 获取欢迎消息内容
        const content = welcomeMsg.innerHTML;

        // 创建新的消息元素，使用bot格式
        const newMessage = document.createElement("div");
        newMessage.className = "ai-customer-chat-message bot";
        newMessage.innerHTML = `
          <div class="ai-customer-chat-bot-avatar"></div>
          <div class="ai-customer-chat-bot-content">${content}</div>
        `;

        // 替换原始欢迎消息
        messagesContainer.replaceChild(newMessage, welcomeMsg);

        // 为选项添加样式和事件
        this._bindWelcomeOptions();
      }
    }

    createChatComponent() {
      // 创建主容器
      const container = document.createElement("div");
      container.className = "ai-customer-chat-container";
      this.container = container;

      // 添加聊天图标按钮（根据配置决定是否显示）
      if (this.options.showFloatButton && !this.options.buttonSelector) {
        const chatButton = document.createElement("div");
        chatButton.className = "ai-customer-chat-button";

        // 使用自定义图片或默认SVG图标
        if (this.options.buttonImageUrl) {
          chatButton.innerHTML = `<img src="${this.options.buttonImageUrl}" alt="客服" width="30" height="30">`;
          chatButton.style.padding = "15px";
        } else {
          chatButton.innerHTML = `<svg viewBox="0 0 24 24" width="24" height="24">
            <path fill="currentColor" d="M20,2H4A2,2 0 0,0 2,4V22L6,18H20A2,2 0 0,0 22,16V4A2,2 0 0,0 20,2M20,16H6L4,18V4H20"/>
          </svg>`;
        }

        container.appendChild(chatButton);
      }

      // 创建聊天框
      const chatBox = document.createElement("div");
      let chatBoxClasses = `ai-customer-chat-box ${
        this.isMobile ? "mobile" : "desktop"
      }`;

      // 如果没有浮动按钮，添加特殊类名用于样式调整
      if (!this.options.showFloatButton) {
        chatBoxClasses += " no-float-button";
      }

      chatBox.className = chatBoxClasses;
      this.chatBox = chatBox;

      // 构建标题区域HTML
      const titleHTML = `
        <div class="ai-customer-chat-header-content">
          ${`<img src="${
            this.options.title ||
            "https://image.kmf.com/test/db/65/db6505e37cdfbf62d01929aadb8a68bb.png"
          }" alt="Title" class="ai-customer-chat-title">`}
          <div class="ai-customer-chat-description">${
            this.options.titleDescription
          }</div>
        </div>`;

      chatBox.innerHTML = `
        <div class="ai-customer-chat-header">
          ${titleHTML}
          <span class="ai-customer-chat-close"></span>
        </div>
        <div class="ai-customer-chat-messages">
          <div class="ai-customer-chat-welcome">
            <p>同学你好，这里是学而思考研，我是你的专属AI考研助手！</p>
            <p>请问你想了解以下哪个方面呢？</p>
            <p class="ai-customer-chat-welcome-options">
                <span>择校择专业</span>
                <span>找学长学姐</span>
                <span>课程咨询</span>
                <span>备考资料</span>
            </p>
          </div>
        </div>
        <div class="ai-customer-chat-input">
          <div class="ai-customer-chat-input-wrapper">
            <textarea placeholder="${this.options.placeholder}" rows="1"></textarea>
            <button class="ai-customer-chat-send">发送</button>
          </div>
          <button class="ai-customer-chat-new">新对话</button>
        </div>
      `;

      container.appendChild(chatBox);
      this.shadowRoot.appendChild(container);
    }

    // 添加缺失的_setupExternalButton方法
    _setupExternalButton() {
      if (this.options.buttonSelector) {
        const externalButton = document.querySelector(
          this.options.buttonSelector
        );
        if (externalButton) {
          // 直接绑定事件，移除复杂的检查逻辑
          externalButton.addEventListener("click", () => this.open());
        }
      }
    }

    bindEvents() {
      // 绑定浮动按钮点击事件
      if (this.options.showFloatButton && !this.options.buttonSelector) {
        const chatButton = this.shadowRoot.querySelector(
          ".ai-customer-chat-button"
        );
        if (chatButton) {
          chatButton.addEventListener("click", () => this.toggle());
        }
      }

      // 添加外部按钮事件绑定
      this._setupExternalButton();

      // 绑定关闭按钮事件
      const closeButton = this.shadowRoot.querySelector(
        ".ai-customer-chat-close"
      );
      closeButton.addEventListener("click", () => this.close());

      // 绑定发送按钮事件
      const sendButton = this.shadowRoot.querySelector(
        ".ai-customer-chat-send"
      );
      const textarea = this.shadowRoot.querySelector("textarea");

      sendButton.addEventListener("click", () => this.sendMessage());
       // 修改: textarea的键盘事件处理
      textarea.addEventListener("keydown", (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          this.sendMessage();
        }
      });
  // 新增: 自动调整textarea高度
      textarea.addEventListener("input", () => {
        this._autoResizeTextarea(textarea);
      });
      // 绑定新对话按钮事件
      const newChatButton = this.shadowRoot.querySelector(
        ".ai-customer-chat-new"
      );
      newChatButton.addEventListener("click", () => this.startNewChat());

      // 绑定欢迎选项点击事件
      this._bindWelcomeOptions();

      // 针对移动端添加触摸事件处理
      if (this.isMobileView()) {
        this._setupMobileEvents();
      }

      // 响应式布局 - 使用节流优化性能
      let resizeTimer;
      window.addEventListener("resize", () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const isMobileNow = this.isMobileView();

          if (this.isMobile !== isMobileNow) {
            this.isMobile = isMobileNow;
            if (isMobileNow) {
              this._setupMobileEvents();
            }
          }

          // 更新聊天框类名，包括no-float-button类
          let chatBoxClasses = `ai-customer-chat-box ${
            isMobileNow ? "mobile" : "desktop"
          }`;
          if (!this.options.showFloatButton) {
            chatBoxClasses += " no-float-button";
          }
          if (this.isOpen) {
            chatBoxClasses += " open";
          }
          this.chatBox.className = chatBoxClasses;

          // 更新样式
          const style = this.shadowRoot.querySelector("style");
          if (style) {
            style.textContent = AiCustomerChatStyles.generateStyles(
              this.options,
              isMobileNow
            );
          }
        }, 150); // 节流延迟
      });
    }
  // 新增: 自动调整textarea高度的方法
    _autoResizeTextarea(textarea) {
      if (!textarea) return;

      // 重置高度以获取正确的scrollHeight
      textarea.style.height = 'auto';
      
      // 计算行高
      const computedStyle = window.getComputedStyle(textarea);
      const lineHeight = parseInt(computedStyle.lineHeight) || 20;
      const paddingTop = parseInt(computedStyle.paddingTop) || 0;
      const paddingBottom = parseInt(computedStyle.paddingBottom) || 0;
      
      // 计算当前内容需要的高度
      const scrollHeight = textarea.scrollHeight;
      
      // 计算最大高度（3行）
      const maxHeight = lineHeight * 3 + paddingTop + paddingBottom;
      
      // 设置新高度
      if (scrollHeight <= maxHeight) {
        textarea.style.height = scrollHeight + 'px';
        textarea.style.overflowY = 'hidden';
      } else {
        textarea.style.height = maxHeight + 'px';
        textarea.style.overflowY = 'auto';
      }
    }

    // 添加移动端特定事件处理
    _setupMobileEvents() {
      // 添加下拉关闭手势
      const header = this.shadowRoot.querySelector(".ai-customer-chat-header");
      let startY = 0;
      let currentY = 0;

      header.addEventListener("touchstart", (e) => {
        startY = e.touches[0].clientY;
      });

      header.addEventListener("touchmove", (e) => {
        currentY = e.touches[0].clientY;
        const diff = currentY - startY;

        // 如果下拉距离超过50px，关闭聊天框
        if (diff > 50) {
          this.close();
        }
      });

      // 优化移动端点击体验，添加点击反馈
      const allClickables = this.shadowRoot.querySelectorAll(
        ".ai-customer-chat-welcome-options span, .ai-customer-chat-send, .ai-customer-chat-new, .ai-customer-chat-button"
      );

      allClickables.forEach((element) => {
        element.addEventListener("touchstart", () => {
          element.style.opacity = "0.7";
        });

        element.addEventListener("touchend", () => {
          setTimeout(() => {
            element.style.opacity = "1";
          }, 100);
        });
      });
    }

    // 简化公共方法
    open() {
      if (!this.chatBox) {
        console.error("聊天框元素未找到");
        return;
      }

      this.isOpen = true;
      this.chatBox.classList.add("open");

      // 触发open事件
      this.dispatchEvent(new CustomEvent("open"));
    }

    close() {
      if (!this.chatBox) return;

      this.isOpen = false;
      this.chatBox.classList.remove("open");

      // 触发close事件
      this.dispatchEvent(new CustomEvent("close"));
    }

    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

     // 修改: 发送消息，添加终止逻辑
    sendMessage() {
      const textarea = this.shadowRoot.querySelector("textarea");
      const message = textarea.value.trim();

      if (message) {
        console.log('发送新消息，准备终止当前对话');
        
        // 1. 先终止当前对话（如果有）
        this._abortCurrentConversation();
        
        // 2. 短暂延迟后发送新消息
        setTimeout(() => {
          if (!this.isProcessing) {
            const messagesContainer = this.shadowRoot.querySelector(".ai-customer-chat-messages");
            const messageElement = document.createElement("div");
            messageElement.className = "ai-customer-chat-message user";
            messageElement.innerHTML = `<p style="white-space: pre-wrap;">${message}</p>`;
            messagesContainer.appendChild(messageElement);
            
            // 清空输入框并重置高度
            textarea.value = "";
            this._autoResizeTextarea(textarea);

            // 用户发送消息时使用平滑滚动
            this._smoothScrollToBottom(messagesContainer);

            // 触发消息发送事件
            this.dispatchEvent(
              new CustomEvent("message", {
                detail: { message: message, type: "user" },
              })
            );

            // 发送API请求处理用户输入
            this._sendToAPI(message, "user");
          }
        }, 100);
      }
    }
  // 新增: 安全地移除DOM元素
    _safeRemoveElement(element) {
      if (element && element.parentNode) {
        try {
          element.parentNode.removeChild(element);
        } catch (error) {
          console.warn('移除DOM元素时出错:', error);
        }
      }
    }
    // 修改: 发送API请求，增强错误处理
    async _sendToAPI(message, type) {
      if (this.isProcessing) return;

      this.isProcessing = true;

      // 创建新的AbortController
      this.currentAbortController = new AbortController();
      const signal = this.currentAbortController.signal;

      // 显示加载指示器
      const loadingElement = this._showLoadingIndicator();

      try {
        // 准备请求参数
        const requestData = {
          stream: true,
          conversation_id: this.conversationId,
          query: message,
        };

        if (type === "option") {
          requestData.input = message;
        } else {
          requestData.input = "";
        }

        // 检查是否有认证token
        const hasAuthToken =
          API_CONFIG.headers.Authorization &&
          API_CONFIG.headers.Authorization.trim() !== "";

        // 如果没有认证token，添加UUID标识临时用户
        if (!hasAuthToken) {
          requestData.uuid = getUserUUID();
        }

        // 发起流式请求，传入signal
        await this._handleStreamResponse(requestData, loadingElement, signal);
      } catch (error) {
        // 检查是否是用户主动取消的请求
        if (error.name === 'AbortError') {
          console.log('请求已被用户取消');
          return;
        }
        
        console.error("API请求失败:", error);

        // 确保移除加载指示器
        if (loadingElement && loadingElement.parentNode) {
          loadingElement.parentNode.removeChild(loadingElement);
        }

        // 显示错误消息
        this._showErrorMessage("抱歉，服务暂时不可用，请稍后再试。");
      } finally {
        this.isProcessing = false;
        this.currentAbortController = null;
      }
    }

    // 修改: 显示加载指示器，增加更好的视觉效果
    _showLoadingIndicator() {
      const messagesContainer = this.shadowRoot.querySelector(
        ".ai-customer-chat-messages"
      );

      const loadingMessage = document.createElement("div");
      loadingMessage.className = "ai-customer-chat-message bot loading-message"; // 添加特殊类名便于识别
      loadingMessage.innerHTML = `
        <div class="ai-customer-chat-bot-avatar"></div>
        <div class="ai-customer-chat-loading">
          <span>正在思考</span>
          <div class="ai-customer-chat-loading-dots">
            <div class="ai-customer-chat-loading-dot"></div>
            <div class="ai-customer-chat-loading-dot"></div>
            <div class="ai-customer-chat-loading-dot"></div>
          </div>
        </div>
      `;

      messagesContainer.appendChild(loadingMessage);
      this._immediateScrollToBottom(messagesContainer);

      console.log("显示加载指示器：正在思考...");
      return loadingMessage;
    }

    // 修改: 智能内容渲染，强制按位置顺序渲染
    _intelligentRenderContent(content, container, isStreaming = true) {
      if (!content || !container) return;

      // 检查内容是否真的发生了变化
      const currentContent = container.textContent || "";
      if (currentContent === content && !content.includes("<")) {
        return; // 内容没有变化，跳过渲染
      }

      // 使用正则表达式分离文本和完整的HTML标签，保持顺序
      const parts = [];
      let lastIndex = 0;
      // 确保每次都创建新的正则表达式对象
      const imgTagRegex = /<img[^>]*>/gi;
      let match;

      // 找到所有完整的img标签
      while ((match = imgTagRegex.exec(content)) !== null) {
        // 添加img标签前的文本
        if (match.index > lastIndex) {
          const textBefore = content.substring(lastIndex, match.index);
          if (textBefore.trim()) {
            parts.push({
              type: "text",
              content: textBefore,
              position: lastIndex,
            });
          }
        }

        // 添加img标签
        parts.push({ type: "img", content: match[0], position: match.index });
        lastIndex = match.index + match[0].length;
      }

      // 添加剩余的文本（可能包含不完整的HTML）
      if (lastIndex < content.length) {
        let remainingText = content.substring(lastIndex);

        // 移除不完整的HTML标签
        remainingText = remainingText.replace(/<[^>]*$/g, "");

        if (remainingText.trim()) {
          parts.push({
            type: "text",
            content: remainingText,
            position: lastIndex,
          });
        }
      }

      // 对于包含图片的内容，总是重新渲染以确保正确的顺序
      if (parts.some((p) => p.type === "img")) {
        console.log("内容包含图片，强制重新渲染");
        this._renderPartsInOrder(parts, container, isStreaming);
      } else {
        // 纯文本内容，只更新文本
        const allText = parts
          .filter((p) => p.type === "text")
          .map((p) => p.content)
          .join("");
        if (allText.trim()) {
          this._updateTextContent(allText, container, isStreaming);
        }
      }
    }
   // 修改: 处理流式响应，增强错误处理
    async _handleStreamResponse(requestData, loadingElement, signal) {
      let response;
      let reader;
      
      try {
        response = await fetch(API_CONFIG.baseUrl, {
          method: "POST",
          headers: API_CONFIG.headers,
          body: JSON.stringify(requestData),
          signal: signal, // 添加取消信号
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        reader = response.body.getReader();
        this.currentReader = reader; // 保存读取器引用
        
      } catch (error) {
        // 如果是 AbortError，说明请求被取消，这是预期的
        if (error.name === 'AbortError') {
          console.log('请求在创建阶段被取消');
          return;
        }
        throw error;
      }
      
      const decoder = new TextDecoder();
      let botMessageElement = null;
      let botContentElement = null;
      let accumulatedContent = "";
      let lastRenderedContent = "";
      let lastImageCount = 0;
      let loadingRemoved = false;

      // 获取消息容器引用
      const messagesContainer = this.shadowRoot.querySelector(".ai-customer-chat-messages");

      // 优化节流设置
      let lastUpdateTime = 0;
      let pendingUpdate = false;
      const UPDATE_INTERVAL = 100;
      let updateRequestId = null;

      try {
        while (true) {
          // 检查是否被取消
          if (signal.aborted) {
            console.log('流式读取被取消');
            break;
          }

          let readResult;
          try {
            readResult = await reader.read();
          } catch (error) {
            // 如果读取被中断，检查是否是预期的取消
            if (error.name === 'AbortError' || signal.aborted) {
              console.log('读取过程中被取消');
              break;
            }
            throw error;
          }

          const { done, value } = readResult;

          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split("\n");

          for (const line of lines) {
            // 再次检查是否被取消
            if (signal.aborted) {
              console.log('处理过程中检测到取消信号');
              break;
            }

            if (line.trim() === "" || !line.startsWith("data: ")) continue;

            try {
              const jsonStr = line.substring(6);

              if (jsonStr.trim() === "[DONE]") {
                // 流式响应结束，进行最终渲染
                if (botContentElement && accumulatedContent && !signal.aborted) {
                  this._renderFinalContent(accumulatedContent, botContentElement);
                  // 最终渲染后确保滚动到底部
                  setTimeout(() => {
                    if (!signal.aborted) {
                      this._forceScrollToBottom(messagesContainer);
                    }
                  }, 100);
                }
                break;
              }

              const data = JSON.parse(jsonStr);

              if (data.object === "bot.workflow.started") {
                this.conversationId = data.conversation_id;
                console.log("工作流已启动，对话ID:", this.conversationId);
              } else if (
                data.object === "bot.message" &&
                data.delta &&
                data.delta.content
              ) {
                // 检查是否被取消
                if (signal.aborted) break;

                // 第一次接收到消息内容时，移除加载指示器并创建消息容器
                if (!loadingRemoved) {
                  // 移除加载指示器
                  if (loadingElement && loadingElement.parentNode) {
                    loadingElement.parentNode.removeChild(loadingElement);
                  }

                  // 创建新的机器人消息元素
                  botMessageElement = document.createElement("div");
                  botMessageElement.className = "ai-customer-chat-message bot";
                  botMessageElement.innerHTML = `
                    <div class="ai-customer-chat-bot-avatar"></div>
                    <div class="ai-customer-chat-bot-content" style="min-height: 20px;"></div>
                  `;
                  messagesContainer.appendChild(botMessageElement);
                  botContentElement = botMessageElement.querySelector(".ai-customer-chat-bot-content");

                  if (!signal.aborted) {
                    this._forceScrollToBottom(messagesContainer);
                  }
                  loadingRemoved = true;
                  console.log("开始接收消息内容，加载指示器已移除");
                }

                // 累积增量内容
                for (const content of data.delta.content) {
                  if (content.type === "text" && content.text && content.text.value) {
                    accumulatedContent += content.text.value;
                  }
                }

                // 确保内容真的发生了变化并且不为空，且没有被取消
                if (
                  botContentElement &&
                  accumulatedContent &&
                  accumulatedContent !== lastRenderedContent &&
                  accumulatedContent.trim() &&
                  !signal.aborted
                ) {
                  const now = Date.now();

                  // 检查当前内容中的图片数量
                  const currentImageCount = (accumulatedContent.match(/<img[^>]*>/gi) || []).length;
                  const hasNewImage = currentImageCount > lastImageCount;

                  // 如果有新图片，立即更新；否则使用节流
                  if (hasNewImage || (now - lastUpdateTime >= UPDATE_INTERVAL && !pendingUpdate)) {
                    // 取消之前的更新请求
                    if (updateRequestId) {
                      cancelAnimationFrame(updateRequestId);
                    }

                    pendingUpdate = true;
                    lastUpdateTime = now;

                    updateRequestId = requestAnimationFrame(() => {
                      // 再次检查是否被取消和内容是否需要更新
                      if (
                        !signal.aborted &&
                        botContentElement &&
                        accumulatedContent !== lastRenderedContent &&
                        accumulatedContent.trim()
                      ) {
                        // 检查用户是否在底部
                        const wasAtBottom = this._isScrolledToBottom(messagesContainer);

                        // 使用优化后的更新方法
                        this._updateStreamingContent(accumulatedContent, botContentElement);
                        lastRenderedContent = accumulatedContent;
                        lastImageCount = currentImageCount;

                        // 如果用户原本在底部，或者有新图片，自动滚动到底部
                        if ((wasAtBottom || hasNewImage) && !signal.aborted) {
                          setTimeout(() => {
                            if (!signal.aborted) {
                              this._forceScrollToBottom(messagesContainer);
                            }
                          }, 50);
                        }
                      }
                      pendingUpdate = false;
                      updateRequestId = null;
                    });
                  }
                }
              } else if (data.object === "bot.workflow.finished") {
                // 检查是否被取消
                if (signal.aborted) break;

                // 工作流结束处理
                if (!loadingRemoved) {
                  if (loadingElement && loadingElement.parentNode) {
                    loadingElement.parentNode.removeChild(loadingElement);
                  }
                  loadingRemoved = true;
                }

                // 优先使用最终答案
                let finalContent = accumulatedContent;

                if (data.data && data.data.outputs && data.data.outputs.answer) {
                  finalContent = data.data.outputs.answer;
                }

                // 如果还没有创建消息容器，现在创建
                if (!botContentElement && finalContent && finalContent.trim() && !signal.aborted) {
                  botMessageElement = document.createElement("div");
                  botMessageElement.className = "ai-customer-chat-message bot";
                  botMessageElement.innerHTML = `
                    <div class="ai-customer-chat-bot-avatar"></div>
                    <div class="ai-customer-chat-bot-content" style="min-height: 20px;"></div>
                  `;
                  messagesContainer.appendChild(botMessageElement);
                  botContentElement = botMessageElement.querySelector(".ai-customer-chat-bot-content");
                }

                if (botContentElement && finalContent && finalContent.trim() && !signal.aborted) {
                  this._renderFinalContent(finalContent, botContentElement);
                }

                // 最终完成后强制滚动到底部
                if (!signal.aborted) {
                  setTimeout(() => {
                    if (!signal.aborted) {
                      this._forceScrollToBottom(messagesContainer);
                    }
                  }, 100);
                }
              }
            } catch (parseError) {
              console.warn("解析SSE数据失败:", parseError, line);
            }
          }

          // 检查外层循环是否被取消
          if (signal.aborted) {
            console.log('外层循环检测到取消信号');
            break;
          }
        }
      } catch (error) {
        // 如果是 AbortError，说明是正常的取消操作
        if (error.name === 'AbortError') {
          console.log('流式处理被正常取消');
          return;
        }
        // 其他错误重新抛出
        throw error;
      } finally {
        // 清理资源
        if (reader && reader !== this.currentReader) {
          // 如果 reader 不是当前的 reader，说明可能已经被替换，小心处理
          try {
            if (!reader.closed) {
              reader.releaseLock();
            }
          } catch (error) {
            console.warn('清理旧读取器时出错:', error);
          }
        }
        
        // 如果这是当前的 reader，清除引用
        if (this.currentReader === reader) {
          this.currentReader = null;
        }
        
        // 清理未完成的更新请求
        if (updateRequestId) {
          cancelAnimationFrame(updateRequestId);
        }
        
        // 确保移除任何残留的加载指示器
        if (loadingElement && loadingElement.parentNode && !loadingRemoved && !signal.aborted) {
          loadingElement.parentNode.removeChild(loadingElement);
        }
      }
    }
     // 新增: 组件销毁时清理资源
    disconnectedCallback() {
      console.log('组件被销毁，清理资源');
      this._abortCurrentConversation();
    }
    // 新增: 强制滚动到底部的方法
    _forceScrollToBottom(container) {
      if (!container) return;

      // 使用更短的延迟，多次尝试确保滚动成功
      const scrollAttempts = [0, 10, 50, 100];

      scrollAttempts.forEach((delay) => {
        setTimeout(() => {
          container.scrollTop = container.scrollHeight;
        }, delay);
      });
    }

    // 修改: 智能更新流式内容，避免不必要的重建
    _updateStreamingContent(content, container) {
      if (!content || !container) return;

      // 检查内容是否包含HTML标签
      if (content.includes("<img") || content.includes("<")) {
        // 包含HTML的情况，使用智能增量更新
        this._smartUpdateHtmlContent(content, container, true);
      } else {
        // 纯文本内容，直接更新文本
        this._smartUpdateTextContent(content, container, true);
      }
    }
    // 新增: 智能更新HTML内容，避免图片闪动
    _smartUpdateHtmlContent(content, container, isStreaming = true) {
      // 解析新内容
      const newParts = this._parseContentToParts(content);

      // 获取当前容器的状态
      const currentImages = container.querySelectorAll("img");
      const currentParagraphs = container.querySelectorAll("p");
      const newImages = newParts.filter((p) => p.type === "img");
      const newTextParts = newParts.filter((p) => p.type === "text");

      // 检查图片是否发生变化
      const imagesChanged = this._checkImagesChanged(currentImages, newImages);

      // 如果图片没有变化，只更新文本内容
      if (!imagesChanged && currentImages.length === newImages.length) {
        console.log("图片未变化，只更新文本内容");
        this._updateOnlyTextParts(newTextParts, container, isStreaming);
      } else {
        // 图片发生变化，需要重新渲染
        console.log("图片发生变化，重新渲染整个容器");
        this._renderPartsInOrder(newParts, container, isStreaming);
      }
    }
    // 新增: 解析内容为parts
    _parseContentToParts(content) {
      const parts = [];
      let lastIndex = 0;
      const imgTagRegex = /<img[^>]*>/gi;
      let match;

      while ((match = imgTagRegex.exec(content)) !== null) {
        // 添加img标签前的文本
        if (match.index > lastIndex) {
          const textBefore = content.substring(lastIndex, match.index);
          if (textBefore.trim()) {
            parts.push({
              type: "text",
              content: textBefore,
              position: lastIndex,
            });
          }
        }

        // 添加img标签
        parts.push({
          type: "img",
          content: match[0],
          position: match.index,
        });
        lastIndex = match.index + match[0].length;
      }

      // 处理剩余文本
      if (lastIndex < content.length) {
        let remainingText = content.substring(lastIndex);
        remainingText = remainingText.replace(/<[^>]*$/g, "");

        if (remainingText.trim()) {
          parts.push({
            type: "text",
            content: remainingText,
            position: lastIndex,
          });
        }
      }

      return parts.sort((a, b) => a.position - b.position);
    }

    // 新增: 检查图片是否发生变化
    _checkImagesChanged(currentImages, newImageParts) {
      if (currentImages.length !== newImageParts.length) {
        return true;
      }

      for (let i = 0; i < currentImages.length; i++) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = newImageParts[i].content;
        const newImgElement = tempDiv.querySelector("img");

        if (!newImgElement || currentImages[i].src !== newImgElement.src) {
          return true;
        }
      }

      return false;
    }
    // 新增: 只更新文本部分，保持图片不变
    _updateOnlyTextParts(textParts, container, isStreaming) {
      // 合并所有文本内容
      const allText = textParts.map((p) => p.content).join("");

      // 获取现有的p标签
      const existingParagraphs = container.querySelectorAll("p");

      // 按段落分割新文本
      const newParagraphs = allText.split(/\n\s*\n/).filter((p) => p.trim());

      // 找到最后一个段落索引
      const lastParagraphIndex = newParagraphs.length - 1;

      // 更新现有段落或创建新段落
      for (
        let i = 0;
        i < Math.max(existingParagraphs.length, newParagraphs.length);
        i++
      ) {
        if (i < newParagraphs.length) {
          const paragraphContent = newParagraphs[i].replace(/\n/g, "<br>");
          const shouldShowCursor = isStreaming && i === lastParagraphIndex;

          if (i < existingParagraphs.length) {
            // 更新现有段落
            const paragraph = existingParagraphs[i];
            if (paragraph.innerHTML !== paragraphContent) {
              paragraph.innerHTML = paragraphContent;
            }

            // 更新光标状态
            if (
              shouldShowCursor &&
              !paragraph.classList.contains("streaming-text")
            ) {
              paragraph.className = "streaming-text";
            } else if (
              !shouldShowCursor &&
              paragraph.classList.contains("streaming-text")
            ) {
              paragraph.className = "";
            }
          } else {
            // 创建新段落（插入到正确位置）
            const newP = document.createElement("p");
            newP.style.margin = "0 0 8px 0";
            newP.style.lineHeight = "1.5";
            newP.innerHTML = paragraphContent;
            newP.className = shouldShowCursor ? "streaming-text" : "";

            // 找到合适的插入位置（在图片之前或容器末尾）
            this._insertParagraphAtCorrectPosition(newP, container, i);
          }
        } else {
          // 移除多余的段落
          if (existingParagraphs[i]) {
            existingParagraphs[i].remove();
          }
        }
      }
    }
    // 新增: 在正确位置插入段落
    _insertParagraphAtCorrectPosition(paragraph, container, paragraphIndex) {
      const children = Array.from(container.childNodes);
      let textNodeCount = 0;
      let insertPosition = container.childNodes.length;

      // 找到正确的插入位置
      for (let i = 0; i < children.length; i++) {
        if (children[i].tagName === "P") {
          textNodeCount++;
        }

        if (textNodeCount === paragraphIndex) {
          insertPosition = i + 1;
          break;
        }
      }

      if (insertPosition < container.childNodes.length) {
        container.insertBefore(paragraph, container.childNodes[insertPosition]);
      } else {
        container.appendChild(paragraph);
      }
    }
    // 新增: 智能更新纯文本内容
    _smartUpdateTextContent(content, container, isStreaming) {
      const paragraphs = content.split(/\n\s*\n/).filter((p) => p.trim());
      const existingParagraphs = container.querySelectorAll("p");
      const lastParagraphIndex = paragraphs.length - 1;

      // 如果段落数量相同，只更新内容
      if (existingParagraphs.length === paragraphs.length) {
        paragraphs.forEach((paragraph, index) => {
          const processedContent = paragraph.replace(/\n/g, "<br>");
          const shouldShowCursor = isStreaming && index === lastParagraphIndex;

          if (existingParagraphs[index].innerHTML !== processedContent) {
            existingParagraphs[index].innerHTML = processedContent;
          }

          // 更新光标状态
          if (
            shouldShowCursor &&
            !existingParagraphs[index].classList.contains("streaming-text")
          ) {
            existingParagraphs[index].className = "streaming-text";
          } else if (
            !shouldShowCursor &&
            existingParagraphs[index].classList.contains("streaming-text")
          ) {
            existingParagraphs[index].className = "";
          }
        });
      } else {
        // 段落数量不同，重新创建
        this._updateTextContent(content, container, isStreaming);
      }
    }

    // 修改: 增量渲染HTML内容，使用新的智能更新
    _renderPartialHtmlContentIncremental(
      content,
      container,
      isStreaming = true
    ) {
      // 使用新的智能更新方法
      this._smartUpdateHtmlContent(content, container, isStreaming);
    }

    // 修改: 在原位置更新文本内容，优化逻辑
    _updateTextContentInPlace(parts, container, isStreaming) {
      // 使用智能更新方法
      const textParts = parts.filter((p) => p.type === "text");
      this._updateOnlyTextParts(textParts, container, isStreaming);
    }

    // 修改: 按顺序渲染parts，添加防闪动保护
    _renderPartsInOrder(parts, container, isStreaming) {
      // 如果容器已有内容且结构相似，尝试增量更新
      const hasExistingContent = container.childNodes.length > 0;
      const newImages = parts.filter((p) => p.type === "img");
      const currentImages = container.querySelectorAll("img");

      if (
        hasExistingContent &&
        !this._checkImagesChanged(currentImages, newImages)
      ) {
        console.log("结构相似，使用增量更新避免闪动");
        const textParts = parts.filter((p) => p.type === "text");
        this._updateOnlyTextParts(textParts, container, isStreaming);
        return;
      }

      // 需要完全重新渲染
      console.log("完全重新渲染内容");

      // 保存当前滚动位置
      const messagesContainer = this.shadowRoot.querySelector(
        ".ai-customer-chat-messages"
      );
      const scrollTop = messagesContainer ? messagesContainer.scrollTop : 0;

      // 清空容器
      container.innerHTML = "";

      // 按position顺序排序
      parts.sort((a, b) => a.position - b.position);

      // 找到最后一个文本部分的索引
      let lastTextPartIndex = -1;
      for (let i = parts.length - 1; i >= 0; i--) {
        if (parts[i].type === "text") {
          lastTextPartIndex = i;
          break;
        }
      }

      // 按顺序渲染每个部分
      parts.forEach((part, index) => {
        if (part.type === "text") {
          // 将文本按段落分割
          const paragraphs = part.content.split(/\n\s*\n/);

          paragraphs.forEach((paragraph, pIndex) => {
            if (paragraph.trim()) {
              const textElement = document.createElement("p");
              textElement.style.margin = "0 0 8px 0";
              textElement.style.lineHeight = "1.5";

              // 只有最后一个段落且是最后一个文本部分时显示光标
              const isLastParagraph = pIndex === paragraphs.length - 1;
              const shouldShowCursor =
                isStreaming && index === lastTextPartIndex && isLastParagraph;
              textElement.className = shouldShowCursor ? "streaming-text" : "";

              // 处理段落内的换行符
              textElement.innerHTML = paragraph.replace(/\n/g, "<br>");
              container.appendChild(textElement);
            }
          });
        } else if (part.type === "img") {
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = part.content;
          const imgElement = tempDiv.firstChild;
          if (imgElement) {
            this._applyImageStyles(imgElement);
            container.appendChild(imgElement);
          }
        }
      });

      // 恢复滚动位置（如果需要）
      if (messagesContainer && scrollTop > 0) {
        setTimeout(() => {
          messagesContainer.scrollTop = scrollTop;
        }, 0);
      }
    }
    // 修改: 渲染parts到容器，使用p标签
    _renderPartsToContainer(parts, container, isStreaming) {
      // 确保容器是空的
      container.innerHTML = "";

      // 如果parts有position信息，按position排序
      if (parts.length > 0 && parts[0].position !== undefined) {
        parts.sort((a, b) => a.position - b.position);
      }

      // 找到最后一个文本部分的索引
      let lastTextPartIndex = -1;
      for (let i = parts.length - 1; i >= 0; i--) {
        if (parts[i].type === "text") {
          lastTextPartIndex = i;
          break;
        }
      }

      parts.forEach((part, index) => {
        if (part.type === "text") {
          // 将文本按段落分割
          const paragraphs = part.content.split(/\n\s*\n/);

          paragraphs.forEach((paragraph, pIndex) => {
            if (paragraph.trim()) {
              const textElement = document.createElement("p");
              textElement.style.margin = "0 0 8px 0";
              textElement.style.lineHeight = "1.5";

              // 只有最后一个段落且是最后一个文本部分时显示光标
              const isLastParagraph = pIndex === paragraphs.length - 1;
              const shouldShowCursor =
                isStreaming && index === lastTextPartIndex && isLastParagraph;
              textElement.className = shouldShowCursor ? "streaming-text" : "";

              // 处理段落内的换行符
              textElement.innerHTML = paragraph.replace(/\n/g, "<br>");
              container.appendChild(textElement);
            }
          });
        } else if (part.type === "img") {
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = part.content;
          const imgElement = tempDiv.firstChild;
          if (imgElement) {
            this._applyImageStyles(imgElement);
            container.appendChild(imgElement);
          }
        }
      });
    }
    // 修改: 更新文本内容，使用p标签
    _updateTextContent(content, container, isStreaming) {
      // 清空容器
      container.innerHTML = "";

      // 将内容按段落分割
      const paragraphs = content.split(/\n\s*\n/);

      paragraphs.forEach((paragraph, index) => {
        if (paragraph.trim()) {
          const textElement = document.createElement("p");
          textElement.style.margin = "0 0 8px 0";
          textElement.style.lineHeight = "1.5";

          // 只有最后一个段落显示光标
          const isLastParagraph = index === paragraphs.length - 1;
          textElement.className =
            isStreaming && isLastParagraph ? "streaming-text" : "";

          // 处理段落内的换行符
          textElement.innerHTML = paragraph.replace(/\n/g, "<br>");
          container.appendChild(textElement);
        }
      });
    }
    // 修改: 最终内容渲染，避免不必要的重建
    _renderFinalContent(content, container) {
      if (!content || !container) return;

      // 移除所有流式效果
      const streamingElements = container.querySelectorAll(".streaming-text");
      streamingElements.forEach((el) => el.classList.remove("streaming-text"));

      // 处理可能包含的HTML内容（如图片）
      if (content.includes("<img")) {
        const newParts = this._parseContentToParts(content);
        const currentImages = container.querySelectorAll("img");
        const newImages = newParts.filter((p) => p.type === "img");

        // 如果图片没有变化，只更新文本并移除流式效果
        if (!this._checkImagesChanged(currentImages, newImages)) {
          console.log("最终渲染：图片未变化，只更新文本");
          const textParts = newParts.filter((p) => p.type === "text");
          this._updateOnlyTextParts(textParts, container, false);
        } else {
          console.log("最终渲染：图片发生变化，重新渲染");
          this._renderPartsInOrder(newParts, container, false);
        }
      } else {
        // 纯文本内容，使用智能更新
        this._smartUpdateTextContent(content, container, false);
      }
    }

    // 新增: 检查图片是否按正确顺序排列 - 修复比较逻辑
    _areImagesInCorrectOrder(currentImages, newImageParts) {
      if (currentImages.length !== newImageParts.length) {
        return false;
      }

      for (let i = 0; i < currentImages.length; i++) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = newImageParts[i].content;
        const newImgElement = tempDiv.querySelector("img");

        if (newImgElement && currentImages[i].src !== newImgElement.src) {
          return false;
        }
      }

      return true;
    }
    // 新增: 调试内容解析的辅助方法
    _debugContentParsing(content) {
      console.log("原始内容:", content);
      console.log("内容长度:", content.length);

      const imgMatches = content.match(/<img[^>]*>/gi);
      console.log("找到的图片标签:", imgMatches);

      if (imgMatches) {
        imgMatches.forEach((match, index) => {
          const matchIndex = content.indexOf(match);
          console.log(`图片 ${index} 位置:`, matchIndex, "内容:", match);
        });
      }
    }
    // 修改: 显示错误消息，添加动画效果
    _showErrorMessage(message) {
      const messagesContainer = this.shadowRoot.querySelector(
        ".ai-customer-chat-messages"
      );
      const errorElement = document.createElement("div");
      errorElement.className = "ai-customer-chat-message bot";
      errorElement.innerHTML = `
        <div class="ai-customer-chat-bot-avatar"></div>
        <div class="ai-customer-chat-bot-content"><p style="color: #ff6b6b; margin: 0;">${message}</p></div>
      `;
      messagesContainer.appendChild(errorElement);
      this._smoothScrollToBottom(messagesContainer);
    }

      // 修改: 新对话，添加终止逻辑
    startNewChat() {
      console.log('开始新对话，准备终止当前对话');
      
      // 1. 先终止当前对话（如果有）
      this._abortCurrentConversation();
      
      // 2. 短暂延迟后开始新对话
      setTimeout(() => {
        // 清除所有消息
        const messagesContainer = this.shadowRoot.querySelector(".ai-customer-chat-messages");
        messagesContainer.innerHTML = "";

        // 重置对话状态
        this.conversationId = "";
        this.isProcessing = false;

        // 创建并添加欢迎消息
        const welcomeMessage = document.createElement("div");
        welcomeMessage.className = "ai-customer-chat-welcome";
        welcomeMessage.innerHTML = `
          <p>同学你好，这里是学而思考研，我是你的专属AI考研助手！</p>
          <p>请问你想了解以下哪个方面呢？</p>
          <p class="ai-customer-chat-welcome-options">
              <span>择校择专业</span>
              <span>找学长学姐</span>
              <span>课程咨询</span>
              <span>备考资料</span>
          </p>
        `;
        messagesContainer.appendChild(welcomeMessage);

        // 更新欢迎消息为bot格式，并添加事件绑定
        this.updateWelcomeMessage();

        // 清除输入框并重置高度
        const textarea = this.shadowRoot.querySelector("textarea");
        if (textarea) {
          textarea.value = "";
          this._autoResizeTextarea(textarea);
        }

        // 确保滚动到顶部显示欢迎消息
        setTimeout(() => {
          messagesContainer.scrollTop = 0;
        }, 100);

        // 触发新对话事件
        this.dispatchEvent(new CustomEvent("newchat"));

        console.log("新对话已开始，conversationId已重置为空:", this.conversationId);
      }, 100);
    }

    // 修改: 供外部调用的API方法
    addMessage(message, type = "bot") {
      if (!message) return;

      const messagesContainer = this.shadowRoot.querySelector(
        ".ai-customer-chat-messages"
      );
      const messageElement = document.createElement("div");
      messageElement.className = `ai-customer-chat-message ${type}`;

      if (type === "bot") {
        const botContent = document.createElement("div");
        botContent.className = "ai-customer-chat-bot-content";

        messageElement.innerHTML = `<div class="ai-customer-chat-bot-avatar"></div>`;
        messageElement.appendChild(botContent);

        // 使用最终渲染方法
        this._renderFinalContent(message, botContent);
      } else {
        messageElement.innerHTML = `<p style="margin: 0;">${message}</p>`;
      }

      messagesContainer.appendChild(messageElement);
      this._smoothScrollToBottom(messagesContainer);
    }

    // 修改: 检查是否滚动到底部，调整阈值
    _isScrolledToBottom(container, threshold = 100) {
      if (!container) return false;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      return scrollHeight - scrollTop - clientHeight <= threshold;
    }

    // 保留原有的滚动方法，用于用户主动操作
    _immediateScrollToBottom(container) {
      if (!container) return;
      setTimeout(() => {
        container.scrollTop = container.scrollHeight;
      }, 0);
    }
    // 新增: 平滑滚动到底部（仅用于用户主动操作）
    _smoothScrollToBottom(container) {
      if (!container) return;
      setTimeout(() => {
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;
        const maxScrollTop = scrollHeight - clientHeight;

        if (maxScrollTop > 0) {
          container.scrollTo({
            top: scrollHeight,
            behavior: "smooth",
          });
        }
      }, 10);
    }

    // 修改: 格式化消息内容，确保图片样式正确
    _formatMessage(content) {
      if (!content) return "";

      // 创建临时容器解析HTML
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = content;

      // 处理所有图片标签
      const images = tempDiv.querySelectorAll("img");
      images.forEach((img) => {
        const existingStyle = img.getAttribute("style") || "";
        const defaultStyle =
          "max-width: 100%; height: auto; border-radius: 8px; margin: 8px 0; display: block; vertical-align: top;";

        // 合并样式（优先保留原有样式）
        if (!existingStyle.includes("max-width")) {
          img.style.cssText = defaultStyle + " " + existingStyle;
        }
      });

      return tempDiv.innerHTML;
    }

    // 新增: 应用图片样式
    _applyImageStyles(imgElement) {
      const existingStyle = imgElement.getAttribute("style") || "";
      const defaultStyle =
        "max-width: 100%; height: auto; border-radius: 8px; margin: 8px 0; display: block; vertical-align: top;";

      if (!existingStyle.includes("max-width")) {
        imgElement.style.cssText = defaultStyle + " " + existingStyle;
      }
    }

    // 新增: 检查内容是否已经渲染
    _isContentAlreadyRendered(htmlContent, container) {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlContent;
      const newImages = tempDiv.querySelectorAll("img");
      const existingImages = container.querySelectorAll("img");

      if (newImages.length !== existingImages.length) {
        return false;
      }

      for (let i = 0; i < newImages.length; i++) {
        if (newImages[i].src !== existingImages[i].src) {
          return false;
        }
      }

      return true;
    }

    // 新增: 检查内容是否包含完整的HTML标签
    _hasCompleteHtmlTags(content) {
      const imgTagRegex = /<img[^>]*>/gi;
      const matches = content.match(imgTagRegex);

      if (matches) {
        for (const match of matches) {
          if (
            match.includes("src=") &&
            (match.endsWith(">") || match.endsWith("/>"))
          ) {
            return true;
          }
        }
      }

      return false;
    }
  }

  // 注册Web Component
  if (typeof window !== "undefined" && typeof customElements !== "undefined") {
    if (!customElements.get("ai-customer-chat")) {
      customElements.define("ai-customer-chat", AiCustomerChatElement);
    }
  }

  // 保持原有的AiCustomerChat类作为非Web Component实现的向后兼容
  class AiCustomerChat {
    constructor(options = {}) {
      // 创建并插入Web Component
      const chatElement = document.createElement("ai-customer-chat");

      // 设置属性 - 修复showFloatButton的处理
      if (options.autoOpen) chatElement.setAttribute("auto-open", "");
      if (options.position)
        chatElement.setAttribute("position", options.position);
      if (options.title) chatElement.setAttribute("title-image", options.title);
      if (options.titleDescription)
        chatElement.setAttribute("title-description", options.titleDescription);
      if (options.placeholder)
        chatElement.setAttribute("placeholder", options.placeholder);
      if (options.primaryColor)
        chatElement.setAttribute("primary-color", options.primaryColor);

      // 修复showFloatButton属性设置
      if (options.showFloatButton === false) {
        chatElement.setAttribute("showfloatbutton", "false");
      } else if (options.showFloatButton === true) {
        chatElement.setAttribute("showfloatbutton", "true");
      }
      // 保持原有的hide-float-button兼容性
      if (options.showFloatButton === false || !options.showFloatButton) {
        chatElement.setAttribute("hide-float-button", "");
      }

      if (options.buttonImageUrl)
        chatElement.setAttribute("button-image", options.buttonImageUrl);
      if (options.headerBackgroundImage)
        chatElement.setAttribute(
          "header-background",
          options.headerBackgroundImage
        );
      if (options.botAvatarUrl)
        chatElement.setAttribute("bot-avatar-url", options.botAvatarUrl);
      if (options.buttonSelector)
        chatElement.setAttribute("button-selector", options.buttonSelector);

      // 直接添加到DOM
      document.body.appendChild(chatElement);
      this.element = chatElement;
    }

    // 简化方法调用，移除异步等待
    open() {
      if (this.element && typeof this.element.open === "function") {
        this.element.open();
      }
    }

    close() {
      if (this.element) this.element.close();
    }

    toggle() {
      if (this.element) this.element.toggle();
    }

    sendMessage(message) {
      if (message && this.element) this.element.addMessage(message, "user");
    }

    addMessage(message, type) {
      if (this.element) this.element.addMessage(message, type);
    }
  }

  // 支持不同环境加载
  // Vue插件
  const AiCustomerChatPlugin = {
    install(Vue, options = {}) {
      // Vue3检测
      if (Vue.version && Vue.version.startsWith("3.")) {
        Vue.config.globalProperties.$aiChat = new AiCustomerChat(options);
      } else {
        // Vue2
        Vue.prototype.$aiChat = new AiCustomerChat(options);
      }
    },
  };

  // jQuery插件
  if (typeof jQuery !== "undefined") {
    jQuery.fn.aiCustomerChat = function (options = {}) {
      const mergedOptions = { ...options };

      // 如果通过jQuery方法调用，保存选择器用于事件绑定
      if (this.selector || this[0]) {
        // 现代jQuery版本可能不存在this.selector，使用备选方案
        mergedOptions.buttonSelector =
          this.selector || (this[0].id ? `#${this[0].id}` : null);

        // 如果仍然无法确定选择器，为元素添加随机ID并使用它
        if (!mergedOptions.buttonSelector && this[0]) {
          const randomId = "ai-chat-btn-" + Math.floor(Math.random() * 10000);
          this[0].id = randomId;
          mergedOptions.buttonSelector = `#${randomId}`;
        }
      }

      // 创建实例并返回
      return new AiCustomerChat(mergedOptions);
    };
  }

  // 将主类导出到全局
  global.AiCustomerChat = AiCustomerChat;
  global.AiCustomerChatPlugin = AiCustomerChatPlugin;
})(typeof window !== "undefined" ? window : this);
